import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, Loader2 } from "lucide-react";
import {
  USER_PROFILES,
  ACKNOWLEDGMENT_TEXT,
  PRIVACY_POLICY_VERSION,
} from "../../data/countries";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BuyContext = createContext(null);
export const useBuy = () => useContext(BuyContext);

// Minimal fallback list — only used if /api/config fails to load. Backend
// remains the single source of truth; when it responds, this is replaced.
const FALLBACK_COUNTRIES = [
  { code: "US", name: "United States", marketplace_code: "US", is_fallback: false },
  { code: "GB", name: "United Kingdom", marketplace_code: "UK", is_fallback: false },
  { code: "IT", name: "Italy", marketplace_code: "IT", is_fallback: false },
  { code: "DE", name: "Germany", marketplace_code: "DE", is_fallback: false },
  { code: "FR", name: "France", marketplace_code: "FR", is_fallback: false },
  { code: "CA", name: "Canada", marketplace_code: "CA", is_fallback: false },
  { code: "AU", name: "Australia", marketplace_code: "AU", is_fallback: false },
  { code: "IN", name: "India", marketplace_code: "IN", is_fallback: false },
];

// Session-scoped attribution snapshot. Includes ?ref= alongside utm_* so
// every Buy click / Amazon exit / registration keeps the same first-touch
// source through the full journey.
function getAttrSnapshot() {
  const a = getAttribution();
  const utm = { ...a.utm };
  if (a.ref) utm.ref = a.ref; // co-locate for backwards-compatible payloads
  return Object.keys(utm).length ? utm : null;
}

export function BuyProvider({ children }) {
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [fallbackMicrocopy, setFallbackMicrocopy] = useState(
    "Your country does not have a local Amazon marketplace. We'll direct you to " +
      "Amazon.com, where the Kindle edition is available for eligible territories. " +
      "Paperback delivery depends on Amazon's shipping availability to your address."
  );
  const [open, setOpen] = useState(false);
  const [ctaLocation, setCtaLocation] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data?.countries?.length) setCountries(r.data.countries);
        if (r.data?.fallback_microcopy) setFallbackMicrocopy(r.data.fallback_microcopy);
      })
      .catch(() => {});
  }, []);

  const track = useCallback((event, extra = {}) => {
    // Country intentionally omitted: no automatic country signal is used
    // anywhere on the page. The user's chosen country is captured only
    // at submit time through the RegistrationModal form.
    const payload = {
      event,
      utm: getAttrSnapshot(),
      referrer: document.referrer || null,
      path: window.location.pathname,
      ...extra,
    };
    axios.post(`${API}/track`, payload).catch(() => {});
    if (typeof window !== "undefined") {
      if (window.gtag) window.gtag("event", event, extra);
      if (window.fbq && event === "buy_click") window.fbq("track", "InitiateCheckout", extra);
    }
  }, []);

  const openBuy = useCallback((location) => {
    setCtaLocation(location || "unknown");
    setOpen(true);
    track("buy_open", { cta_location: location || "unknown" });
  }, [track]);

  const value = useMemo(
    () => ({ countries, openBuy, track }),
    [countries, openBuy, track]
  );

  return (
    <BuyContext.Provider value={value}>
      {children}
      <RegistrationModal
        open={open}
        onClose={() => setOpen(false)}
        countries={countries}
        fallbackMicrocopy={fallbackMicrocopy}
        ctaLocation={ctaLocation}
        track={track}
      />
    </BuyContext.Provider>
  );
}

// ---------------- Registration modal (form + acknowledgment) ----------------

// International-friendly name check.
// - Allow Unicode letters, spaces, apostrophes (' and ’), hyphens, periods
// - Reject digits and other symbols
const NAME_REGEX = /^[\p{L}\p{M}][\p{L}\p{M}\s'’\-.]*$/u;
const DIGIT_REGEX = /\d/;

function validateName(value) {
  if (!value || !value.trim()) return "This field is required.";
  const trimmed = value.trim();
  if (DIGIT_REGEX.test(trimmed)) return "Name must not contain numbers.";
  if (!NAME_REGEX.test(trimmed)) return "Please use only letters, spaces, apostrophes, or hyphens.";
  return null;
}

function validateEmail(value) {
  if (!value || !value.trim()) return "Email is required.";
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(value.trim())) return "Please enter a valid email address.";
  return null;
}

function RegistrationModal({ open, onClose, countries, fallbackMicrocopy, ctaLocation, track }) {
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [profile, setProfile] = useState("");
  const [profileOther, setProfileOther] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    // Every time the modal is (re)opened, reset the Country field so the
    // user is required to actively choose it. No pre-selection based on
    // timezone, locale, IP or geolocation.
    if (open) setCountry("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Lock the underlying page while preserving scroll position.
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    // Pause Lenis smooth-scroll (which hijacks wheel events on the page)
    // so wheel/trackpad events inside the modal are handled natively.
    const lenis = typeof window !== "undefined" ? window.__lenis : null;
    lenis?.stop?.();

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.right = prev.right;
      document.body.style.width = prev.width;
      document.body.style.overflow = prev.overflow;
      // Restore the exact scroll position the user had before the modal opened.
      window.scrollTo(0, scrollY);
      lenis?.start?.();
    };
  }, [open, onClose, submitting]);

  const validate = () => {
    const e = {};
    const f = validateName(firstName);
    if (f) e.firstName = f;
    const s = validateName(surname);
    if (s) e.surname = s;
    const em = validateEmail(email);
    if (em) e.email = em;
    if (!country) e.country = "Please select your country.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setServerError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        surname: surname.trim(),
        email: email.trim(),
        country,
        user_profile: profile || null,
        user_profile_other:
          profile === "other" && profileOther.trim() ? profileOther.trim() : null,
        marketing_opt_in: !!marketingOptIn,
        cta_location: ctaLocation,
        utm: getAttrSnapshot(),
        referrer: document.referrer || null,
        path: window.location.pathname,
      };
      const { data } = await axios.post(`${API}/register`, payload);
      // fire analytics + persist tracking event (server also logs)
      track("buy_click", {
        marketplace: data?.purchase?.marketplace_code,
        cta_location: ctaLocation,
      });
      // First-party heatmap: Amazon outbound is a separate signal from
      // "Buy Now clicked". The main-Buy click was already reported when
      // openBuy() fired via BuyButton's data-cta attribute; here we mark
      // the actual transition to Amazon.
      try {
        if (typeof window !== "undefined" && window.__sdHeatmapAmazonOutbound) {
          window.__sdHeatmapAmazonOutbound({
            marketplace: data?.purchase?.marketplace_code || null,
            section: "final-cta",
            cta: "buy_click_amazon",
          });
        }
        // Also record in the consented first-party history if that
        // tracker is armed (opt-in via iubenda Measurement).
        if (typeof window !== "undefined" && window.__sdConsentedTracker) {
          window.__sdConsentedTracker.amazonOutbound({
            marketplace: data?.purchase?.marketplace_code || null,
            section_id: "final-cta",
            cta_id: "buy_click_amazon",
          });
        }
      } catch {
        /* never break checkout for analytics */
      }
      const url = data?.purchase?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      onClose();
      // reset form
      setFirstName("");
      setSurname("");
      setEmail("");
      setCountry("");
      setProfile("");
      setProfileOther("");
      setMarketingOptIn(false);
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        setErrors((prev) => ({ ...prev, [detail.field === "first_name" ? "firstName" : detail.field]: detail.message }));
      } else {
        setServerError("We couldn't complete your request. Please try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="registration-modal"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !submitting && onClose()}
            data-testid="registration-modal-overlay"
          />
          <motion.div
            className="relative w-full sm:max-w-2xl bg-secondary gold-border rounded-t-2xl sm:rounded-2xl p-6 sm:p-10 max-h-[92vh] overflow-y-auto"
            style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
            data-lenis-prevent
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              className="absolute top-5 right-5 text-muted hover:text-gold transition-colors"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
              data-testid="registration-modal-close"
              disabled={submitting}
            >
              <X size={22} />
            </button>

            <p className="label mb-3">Join the Self Doubling Growing Circle</p>
            <h3
              className="font-display text-xl sm:text-2xl md:text-3xl leading-tight text-gold mb-5 pr-10"
              data-testid="registration-modal-heading"
            >
              SELF DOUBLING© – THE TRUTH THAT DWELLS WITHIN YOU
            </h3>

            <div className="space-y-3.5 text-secondary text-sm sm:text-base leading-relaxed mb-6">
              <p>
                Self Doubling© is an integral part of the Mosca Method©.
              </p>
              <p>
                The information you provide, including your country, allows us first of all to
                direct you to the appropriate Amazon marketplace for your location, where you can
                purchase Self Doubling – The Truth That Dwells Within You.
              </p>
              <p>
                Your copy of Self Doubling is also your gateway to the Self Doubling Growing
                Circle, the private space created for verified readers of the book.
              </p>
              <p>
                Inside the Growing Circle, you can connect with Maria Gioia Mosca and other
                readers, share questions, reflections and experiences, and access dedicated
                articles, conversations, events and initiatives related to Self Doubling© and
                the Mosca Method©.
              </p>
              <p data-testid="registration-intro-verification-link">
                After continuing to Amazon, you will receive an email containing your personal
                verification link. Once you have purchased the book, use that link to verify
                your copy and request access to the Self Doubling Growing Circle.
              </p>
              <p className="font-semibold text-gold pt-1">How verification works</p>
              <p>
                If you purchase the Kindle edition, upload a screenshot or photo showing Self
                Doubling in your Kindle library or Kindle app.
              </p>
              <p>
                If you purchase the paperback edition, upload a photo of yourself holding the
                book.
              </p>
              <p>
                The image is used only to verify your copy and access to the Self Doubling
                Growing Circle, and is deleted after the verification process.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <FieldWrapper
                  label="First name"
                  helper="Please enter your real first name."
                  error={errors.firstName}
                  htmlFor="reg-first-name"
                  required
                >
                  <input
                    id="reg-first-name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="reg-input"
                    data-testid="reg-first-name"
                    disabled={submitting}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label="Surname"
                  helper="Please enter your real surname."
                  error={errors.surname}
                  htmlFor="reg-surname"
                  required
                >
                  <input
                    id="reg-surname"
                    type="text"
                    autoComplete="family-name"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    className="reg-input"
                    data-testid="reg-surname"
                    disabled={submitting}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label="Email"
                  error={errors.email}
                  htmlFor="reg-email"
                  required
                >
                  <input
                    id="reg-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="reg-input"
                    data-testid="reg-email"
                    disabled={submitting}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label="Country"
                  error={errors.country}
                  htmlFor="reg-country"
                  required
                >
                  <select
                    id="reg-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="reg-input"
                    data-testid="reg-country"
                    disabled={submitting}
                  >
                    <option value="">Select</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const selected = countries.find((c) => c.code === country);
                    if (!selected || !selected.is_fallback) return null;
                    return (
                      <span
                        className="block mt-2 text-xs text-muted leading-relaxed"
                        data-testid="fallback-microcopy"
                      >
                        {fallbackMicrocopy}
                      </span>
                    );
                  })()}
                </FieldWrapper>

                <div className="sm:col-span-2">
                  <FieldWrapper
                    label="How are you approaching Self Doubling?"
                    helper="Optional — this helps us better understand the readers joining the Growing Circle and develop relevant content and initiatives over time."
                    htmlFor="reg-profile"
                  >
                    <select
                      id="reg-profile"
                      value={profile}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProfile(v);
                        if (v !== "other") setProfileOther("");
                      }}
                      className="reg-input"
                      data-testid="reg-profile"
                      disabled={submitting}
                    >
                      <option value="">Select</option>
                      {USER_PROFILES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </FieldWrapper>
                </div>

                {profile === "other" && (
                  <div className="sm:col-span-2">
                    <FieldWrapper
                      label="Please specify"
                      helper="Please describe your context briefly. Do not include health, religious or other sensitive personal information."
                      htmlFor="reg-profile-other"
                    >
                      <input
                        id="reg-profile-other"
                        type="text"
                        maxLength={280}
                        value={profileOther}
                        onChange={(e) => setProfileOther(e.target.value)}
                        className="reg-input"
                        data-testid="reg-profile-other"
                        disabled={submitting}
                      />
                    </FieldWrapper>
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-start gap-3">
                <input
                  id="reg-marketing-opt-in"
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gold/40 bg-transparent accent-[var(--gold)] cursor-pointer flex-shrink-0"
                  data-testid="reg-marketing-opt-in"
                  disabled={submitting}
                />
                <label
                  htmlFor="reg-marketing-opt-in"
                  className="text-secondary leading-relaxed cursor-pointer"
                  style={{ fontSize: "0.9rem" }}
                >
                  I would like to receive occasional news and updates by email about Self
                  Doubling©, the Mosca Method©, publications, events and initiatives by Maria
                  Gioia Mosca. I can unsubscribe at any time.
                </label>
              </div>

              <p
                className="mt-6 text-secondary leading-relaxed"
                style={{ fontSize: "0.9rem" }}
                data-testid="privacy-acknowledgment"
              >
                By clicking &ldquo;CONTINUE TO AMAZON&rdquo;, you confirm that you have read the{" "}
                <a
                  href="https://www.iubenda.com/privacy-policy/84007906"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold underline underline-offset-2 hover:opacity-80"
                  data-testid="privacy-policy-link"
                >
                  Privacy Policy
                </a>{" "}
                and understand that the information you provide will be processed to direct you
                to the appropriate Amazon marketplace, send you the information required to
                verify your copy of Self Doubling, and enable your request for access to the
                Self Doubling Growing Circle.
              </p>

              {serverError && (
                <p className="mt-4 text-sm text-red-400" data-testid="registration-server-error">
                  {serverError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="registration-submit"
                className="btn-gold mt-5 w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-body font-semibold uppercase tracking-wider text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] focus:ring-offset-2 focus:ring-offset-[var(--bg-secondary)]"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Continue to Amazon
                    <ArrowUpRight size={18} />
                  </>
                )}
              </button>

              <input
                type="hidden"
                name="privacy_policy_version"
                value={PRIVACY_POLICY_VERSION}
                readOnly
              />
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function FieldWrapper({ label, helper, error, htmlFor, required, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="label block mb-1.5">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </span>
      {children}
      {helper && !error && (
        <span className="block mt-1 text-xs text-muted">{helper}</span>
      )}
      {error && (
        <span
          className="block mt-1 text-xs text-red-400"
          data-testid={`error-${htmlFor}`}
        >
          {error}
        </span>
      )}
    </label>
  );
}
